import * as React from "react";
import { cn } from "@/lib/utils";

export interface StepIndicatorStep {
  key: string;
  /** 현재 스텝일 때만 번호 옆에 노출되는 라벨 */
  title: string;
}

interface StepIndicatorProps {
  steps: StepIndicatorStep[];
  /** 0-based */
  currentStep: number;
  className?: string;
}

/** 번호 원 — 지나온 스텝은 그라디언트, 남은 스텝은 회색 */
function StepNumber({ index, reached }: { index: number; reached: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-7 shrink-0 items-center justify-center rounded-full",
        "typo-heading-2-semibold inset-border-double",
        reached
          ? "bg-primary-gradient text-static-white"
          : "bg-fills-strong text-labels-assistive",
      )}
    >
      {index + 1}
    </span>
  );
}

/**
 * `①—②—③` 형태의 스텝 표시. 현재 스텝만 라벨을 노출하고 나머지는 번호만 보여준다.
 *
 * ```
 * ① 고객사 검색 ——②——③        (currentStep=0)
 * ①——② 사업자 정보 확인 ——③   (currentStep=1)
 * ```
 */
export function StepIndicator({
  steps,
  currentStep,
  className,
}: StepIndicatorProps) {
  if (steps.length === 0) return null;

  // 호출부가 범위를 벗어난 값을 넘겨도(스텝 배열이 런타임에 교체되는 경우) 렌더는 깨지지 않게 한다
  const activeIndex = Math.min(Math.max(currentStep, 0), steps.length - 1);

  return (
    <div
      className={cn("flex h-7 items-center", className)}
      role="group"
      aria-label={`전체 ${steps.length}단계 중 ${activeIndex + 1}단계`}
    >
      {steps.map((step, index) => {
        const isActive = index === activeIndex;
        const reached = index <= activeIndex;

        return (
          <React.Fragment key={step.key}>
            {index > 0 && (
              <span
                aria-hidden
                className="h-px w-10 shrink-0 bg-lines-solid-normal"
              />
            )}
            {isActive ? (
              <span className="flex items-center gap-2 pr-2">
                <StepNumber index={index} reached />
                <span
                  aria-current="step"
                  className="typo-heading-1-semibold whitespace-nowrap text-primary-strong"
                >
                  {step.title}
                </span>
              </span>
            ) : (
              <StepNumber index={index} reached={reached} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

StepIndicator.displayName = "StepIndicator";
